import {
  getFirestore,
  collection,
  getCountFromServer,
  getDocs,
  doc,
  getDoc,
  type DocumentData,
} from 'firebase/firestore';

import { getAuth, onAuthStateChanged, getIdTokenResult } from 'firebase/auth';

import {
  getStorage,
  ref,
  listAll,
  getDownloadURL,
  getMetadata,
  updateMetadata,
  type StorageReference,
} from 'firebase/storage';

import type { UserSubscription } from './admin.d';
import { getFbFunctions } from './development';
import { httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import type { DashboardMetricsResponse, GlobalUser, ImpersonationResponse, ipifyResponse, ModeratorSlot, StripeSubscription } from './types';


export async function watchSystemPermissions() {
  const fbApp = getApp(this.firebaseApp);
  const auth = getAuth(fbApp);

  onAuthStateChanged(auth, async (user) => {
    if (user) {
      try {
        const decodedToken = await getIdTokenResult(user, true);
        this.system.permissions = decodedToken.claims?.system_permissions as string[] || [];
      } catch {
        this.system.permissions = [];
      }

    } else {
      this.system.permissions = [];
    }
  });
}

export async function getModeratorQueue() {
  // Recursively scan files in global/images/moderation_queue
  const fbApp = getApp(this.firebaseApp);
  const storage = getStorage(fbApp);

  const userListRef = ref(storage, 'global/images/moderation_queue/user');
  const teamListRef = ref(storage, 'global/images/moderation_queue/team');

  const scanFolder = async (folderRef: StorageReference):
  Promise<ModeratorSlot[]> => {
    const results = [] as ModeratorSlot[];
    const list = await listAll(folderRef);

    // Process files in the current folder
    await Promise.all(list.items.map(async (item) => {
      const downloadURL = await getDownloadURL(item);
      const metadata = await getMetadata(item);
      results.push({
        name: item.name,
        fullPath: item.fullPath,
        url: downloadURL,
        metadata: metadata,
      });
    }));

    // Recursively process subfolders
    await Promise.all(list.prefixes.map(async (subFolder) => {
      const subFolderResults = await scanFolder(subFolder);
      results.push(...subFolderResults);
    }));

    return results;
  };

  const userResults = await scanFolder(userListRef);
  const teamResults = await scanFolder(teamListRef);
  return [...userResults, ...teamResults];
}

export async function moderateFile(
  { fileGuid, action, team, user }:
  { fileGuid: string, action: string, team: string, user: string },
): Promise<{ success: boolean, message?: string }> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);

  try {
    const moderate = httpsCallable(functions, 'admin-moderateFile');
    const result = await moderate({ fileGuid, action, team, user }) as { data: { success: boolean, message?: string } };
    return result.data;
  } catch (error: any) {
    console.error(error);
    return { success: false, message: error.message };
  }
}

export async function editFileMetadata(
  { fullPath, metadata }:
  { fullPath: string, metadata: {
    label: string;
    slotSize: string;
    manufacturer: string;
    category: string;
    bodyColor?: string | null;
    rearType?: string | null;
    powerConsumption?: string | null;
    depth?: string | null;
    depthUnit?: string | null;
    weight?: string | null;
    weightUnit?: string | null;
    halfWidth: string;
    orientation?: string | null;
    verticalHeight?: string | null;
    verticalHeightUnit?: string | null;
  } },
): Promise<{ success: boolean, message?: string }> {
  const fbApp = getApp(this.firebaseApp);
  const storage = getStorage(fbApp);
  const fileRef = ref(storage, fullPath);
  const numericSlotMetadataFields = new Set(['powerConsumption', 'depth', 'weight']);
  const normalizeNumericMetadata = (value: unknown) => {
    const match = String(value).replace(/,/g, '').match(/[0-9]+(?:\.[0-9]+)?/);
    return match?.[0] ?? '';
  };
  const customMetadata = Object.fromEntries(
    Object.entries(metadata).map(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        return [key, null];
      }

      const processedValue = numericSlotMetadataFields.has(key)
        ? normalizeNumericMetadata(value)
        : String(value);

      return [key, processedValue === '' ? null : processedValue];
    }),
  );

  try {
    await updateMetadata(fileRef, {
      customMetadata: customMetadata as Record<string, string>,
    });
    return {
      success: true,
    };
  } catch (error: any) {
    return {
      success: false,
      message: error.message,
    };
  }
}

export async function cropType(data: { image: string, fullPath: string }) {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);

  const editType = httpsCallable(functions, 'admin-editType');

  try {
    const result = await editType({
      image: data.image,
      filePath: data.fullPath,
    });

    return result.data;
  } catch (e: any) {
    throw new Error(e);
  }
}

export async function impersonateUser({ user, token }: { user: string, token: string }) {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);

  const impersonate = httpsCallable(functions, 'admin-generateImpersonationToken');

  const { UAParser } = await import('@ua-parser-js/pro-business');
  const ua = new UAParser(navigator.userAgent);
  const device = ua.getResult();
  const ipify = await fetch('https://api.ipify.org?format=json');
  const ip = (await ipify.json() as ipifyResponse)?.ip || 'Unknown';

  try {
    const result = await impersonate({
      user,
      device: device.browser.name + ' on ' + device.os.name,
      ip,
      token,
    }) as ImpersonationResponse;

    if (result?.data?.token) {
      await this.loginWithToken({ token: result.data.token});
      window.location.href = '/app/dashboard';
    } else {
      throw new Error('No token returned');
    }
  } catch (e: any) {
    throw new Error(e);
  }
}

export async function getUsers(): Promise<GlobalUser[]> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);
  const db = getFirestore(fbApp, this.dbid);

  const getUsers = httpsCallable(functions, 'admin-getGlobalUsers');

  try {
    const result = await getUsers();
    const members = result.data as GlobalUser[];

    return await Promise.all(
      members.map(async (member) => {
        const rackRef = collection(db, 'users', member.uid, 'racks');
        const roomRef = collection(db, 'users', member.uid, 'rooms');
        let rackCount = 0, roomCount = 0;

        try {
          const snapshot = await getCountFromServer(rackRef);
          rackCount = snapshot.data().count;
        } catch {
          // User has no racks
        }

        try {
          const snapshot = await getCountFromServer(roomRef);
          roomCount = snapshot.data().count;
        } catch {
          // User has no rooms
        }

        return {
          key: member.uid,
          ...member,
          rackCount,
          roomCount,
          subscription: member?.customClaims?.stripeRole || 'free',
          status: member?.disabled ? 'disabled' : member?.emailVerified ? 'active' : 'pending',
          subscriptions: [],
        };
      }),
    );
  } catch (e: any) {
    throw new Error(e);
  }
}

export async function getTeams() {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  try {
    const teamsRef = collection(db, 'teams');
    const querySnapshot = await getDocs(teamsRef);

    return await Promise.all(
      querySnapshot.docs.map(async (team) => {
        const rackRef = collection(db, 'teams', team.id, 'racks');
        const roomRef = collection(db, 'teams', team.id, 'rooms');
        let rackCount = 0, roomCount = 0;

        try {
          const snapshot = await getCountFromServer(rackRef);
          rackCount = snapshot.data().count;
        } catch {
          // User has no racks
        }

        try {
          const snapshot = await getCountFromServer(roomRef);
          roomCount = snapshot.data().count;
        } catch {
          // User has no rooms
        }

        return {
          key: team.id,
          ...team.data(),
          rackCount,
          roomCount,
          members: team.data().members?.length || 0,
          editors: team.data().editors?.length || 0,
          admins: team.data().admins?.length || 0,
          domain: team.data().customDomain || null,

        };
      }),
    );
  } catch (e: any) {
    throw new Error(e);
  }
}

export async function getGlobalUser(uid: string): Promise<GlobalUser> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);
  const db = getFirestore(fbApp, this.dbid);
  const storage = getStorage(fbApp);

  const getUser = httpsCallable(functions, 'admin-getGlobalUser');
  let userDoc = {} as DocumentData;

  try {
    const result = await getUser({ uid });
    const member = result.data as GlobalUser;
    const subscriptions = [] as UserSubscription[];

    const userRef = doc(db, 'users', member.uid);
    const rackRef = collection(db, 'users', member.uid, 'racks');
    const roomRef = collection(db, 'users', member.uid, 'rooms');
    const subsRef = collection(db, 'users', member.uid, 'subscriptions');
    let rackCount = 0, roomCount = 0, subsCount = 0, typeCount = 0;

    try {
      const docSnap = await getDoc(userRef);
      if (docSnap.exists()) {
        userDoc = docSnap.data();
      }
    } catch (e: any) {
      throw new Error(e);
    }

    try {
      const snapshot = await getCountFromServer(rackRef);
      rackCount = snapshot.data().count;
    } catch {
      // User has no racks
    }

    try {
      const snapshot = await getCountFromServer(roomRef);
      roomCount = snapshot.data().count;
    } catch {
      // User has no rooms
    }

    try {
      const snapshot = await getCountFromServer(subsRef);
      subsCount = snapshot.data().count;
    } catch {
      // User has no subscriptions
    }

    try {
      const docSnap = await getDocs(subsRef);
      const subs = [] as UserSubscription[];
      docSnap.forEach((doc) => {
        const curSub = doc.data() as StripeSubscription;
        subs.push({
          id: doc.id,
          ...curSub,
        });
      });
      subscriptions.push(...subs);
    } catch (e: any) {
      throw new Error(e);
    }

    try {
      const userRef = ref(storage, `user/${member.uid}/images/types`);
      const res = await listAll(userRef);
      typeCount = res.items.length;
    } catch (e: any) {
      throw new Error(e);
    }

    return {
      key: member.uid,
      ...member,
      userDoc,
      rackCount,
      roomCount,
      subsCount,
      typeCount,
      subscription: member?.customClaims?.stripeRole || 'free',
      status: member?.disabled ? 'disabled' : member?.emailVerified ? 'active' : 'pending',
      subscriptions,
    };
  } catch (e: any) {
    throw new Error(e);
  }
}

export async function getGlobalTeam(teamId: string) {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);
  const storage = getStorage(fbApp);

  try {
    const teamRef = doc(db, 'teams', teamId);
    const rackRef = collection(db, 'teams', teamId, 'racks');
    const roomRef = collection(db, 'teams', teamId, 'rooms');
    let rackCount = 0, roomCount = 0, typeCount = 0;
    let teamDoc = {} as DocumentData;

    try {
      const docSnap = await getDoc(teamRef);
      if (docSnap.exists()) {
        teamDoc = docSnap.data();
      }
    } catch (e: any) {
      throw new Error(e);
    }

    try {
      const snapshot = await getCountFromServer(rackRef);
      rackCount = snapshot.data().count;
    } catch {
      // User has no racks
    }

    try {
      const snapshot = await getCountFromServer(roomRef);
      roomCount = snapshot.data().count;
    } catch {
      // User has no rooms
    }

    try {
      const teamRef = ref(storage, `teams/${teamId}/images/types`);
      const res = await listAll(teamRef);
      typeCount = res.items.length;
    } catch (e: any) {
      throw new Error(e);
    }

    return {
      key: teamId,
      ...teamDoc,
      rackCount,
      roomCount,
      typeCount,
      membersCount: teamDoc.members?.length || 0,
      editorsCount: teamDoc.editors?.length || 0,
      adminsCount: teamDoc.admins?.length || 0,
    };
  } catch (e: any) {
    throw new Error(e);
  }
}

export async function getUserSubscriptions(uid: string): Promise<UserSubscription[]> {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);

  const subRef = collection(db, 'users', uid, 'subscriptions');

  try {
    const docSnap = await getDocs(subRef);
    const subs = [] as UserSubscription[];
    docSnap.forEach((doc) => {
      const curSub = doc.data() as StripeSubscription;
      subs.push({
        id: doc.id,
        ...curSub,
      });
    });
    return subs;
  } catch (e: any) {
    throw new Error(e);
  }
}

export async function setSystemPermissions(
  { user, token, permission, action }:
  { user: string, token: string, permission: string, action: string },
) {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);

  const setPermissions = httpsCallable(functions, 'admin-setSystemPermissions');

  const { UAParser } = await import('@ua-parser-js/pro-business');
  const ua = new UAParser(navigator.userAgent);
  const device = ua.getResult();
  const ipify = await fetch('https://api.ipify.org?format=json');
  const ip = (await ipify.json() as ipifyResponse)?.ip || 'Unknown';

  try {
    const result = await setPermissions({
      user,
      device: device.browser.name + ' on ' + device.os.name,
      ip,
      token,
      permission,
      action,
    }) as { data: { success: boolean, message?: string } };

    return result.data;
  } catch (e: any) {
    throw new Error(e);
  }
}

export async function getDashboardMetrics(): Promise<DashboardMetricsResponse> {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);

  const getMetrics = httpsCallable(functions, 'admin-getDashboardMetrics');

  try {
    const result = await getMetrics();
    return result.data as DashboardMetricsResponse;
  } catch (e: any) {
    throw new Error(e);
  }
}
